import React, { Component } from 'react';
import {
  Form,
  Button,
  Upload,
  Icon,
  DatePicker,
  Input,
  InputNumber,
  Select,
  Switch,
  Radio,
  Tooltip,
  Collapse
} from 'antd';
import moment from 'moment';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';

import { LICENSE_TYPE_JWT } from '../../constants';
import { byteConversion } from '../../utils';

import styles from './LicenseCreate.module.scss';

const { Option } = Select;
const { Panel } = Collapse;

const customPanelStyle = {
  background: '#f7f7f7',
  marginBottom: 24,
  borderRadius: 4,
  border: 0,
  textAlign: 'center',
  overflow: 'hidden'
};

// Different licenses have different payload config.
// If the property is true, which means needed. If the property is non-boolean value,
// which means it is non-user input but the default value when submitting
const licensePayloadConfig = {
  [LICENSE_TYPE_JWT]: {
    Use: true,
    Audience: true,
    ValidDays: true,
    Version: '3.0.0',
    ContractID: true,
    Seed: true,
    GraphStudio: true,
    Insights: true,
    GSQLShell: true,
    GraphQL: true,
    MaxGraph: true,
    MaxClusterNode: true,
    MaxTopologySizeByte: true,
    MaxEdge: true,
    MaxVertex: true,
    MaxCPUCore: true,
    MaxTotalCPUs: true,
    CRR: true,
    DataHA: true,
    MaxPhysicalMemorySizeByte: true,
    MaxRSSMemorySizeByte: true,
    ClusterInfo: []
  },
  string: {
    Use: true,
    Audience: true,
    ValidDays: true,
    ContractID: true,
    MaxVertex: true,
    MaxEdge: true,
    MaxTopologySizeByte: true,
    MaxGraph: true,
    ClusterInfo: []
  },
  gst: {
    Use: true,
    Audience: true,
    ValidDays: true,
    ContractID: true,
    ClusterInfo: []
  }
};

class LicenseCreateForm extends Component {
  state = {
    selectedType: this.props.Type || LICENSE_TYPE_JWT,
    selectedUsePurpose: this.props.Use || 'Internal',
    seedUploadList: [],
    contractUploadList: []
  };

  isNewEntryForSeed = true;
  isNewEntryForContract = true;
  skipFileUpload = false;

  handleLicenseTypeSelect = e => {
    const type = e.target.value;

    this.setState({
      selectedType: type
    });
  };

  handleLicenseUsePurposeChange = use => {
    this.setState({
      selectedUsePurpose: use
    });
  };

  handleSubmit = e => {
    e.preventDefault();

    // `validateFields` will trigger all the validator functions.
    // By using `this.skipFileUpload`, we can avoid unnecessary call to upload().
    this.skipFileUpload = true;

    this.props.form.validateFields((err, fieldValues) => {
      if (err) return;

      delete fieldValues.ContractID;

      let data = {};

      const endTime = moment(fieldValues.ValidDays);
      const today = moment().startOf('day');

      const {
        signature,
        Signature: signatureInParam,
        contractID,
        ContractID: contractIDInParam
      } = this.props;

      const signatureToSubmit =
        this.isNewEntryForSeed && signatureInParam
          ? signatureInParam
          : signature.length !== 0
          ? signature
          : [];

      const contractIDToSubmit =
        this.isNewEntryForContract && contractIDInParam
          ? contractIDInParam
          : this.state.contractUploadList.length !== 0
          ? contractID
          : '';

      switch (this.state.selectedType) {
        case LICENSE_TYPE_JWT: {
          data = {
            ...fieldValues,
            ValidDays: endTime.diff(today, 'days'),
            ContractID: contractIDToSubmit,
            // convert GB to Byte
            MaxPhysicalMemorySizeByte: fieldValues.MaxPhysicalMemorySizeByte * 2 ** 30,
            MaxRSSMemorySizeByte: fieldValues.MaxRSSMemorySizeByte * 2 ** 30,
            MaxTopologySizeByte: fieldValues.MaxTopologySizeByte * 2 ** 30,
            Signature: signatureToSubmit,
            Version: '3.0.0',
            ClusterInfo: []
          };

          break;
        }
        case 'string': {
          data = {
            ...fieldValues,
            ValidDays: endTime.diff(today, 'days'),
            ContractID: contractIDToSubmit,
            MaxTopologySizeByte: fieldValues.MaxTopologySizeByte * 2 ** 30,
            ClusterInfo: []
          };
          break;
        }
        default: {
          data = {
            ...fieldValues,
            ValidDays: endTime.diff(today, 'days'),
            ContractID: contractIDToSubmit,
            ClusterInfo: []
          };
        }
      }

      this.props.createLicense(this.state.selectedType, data);
      this.props.closeModal();
    });
  };

  normalizeSeedFile = e => {
    if (Array.isArray(e)) {
      this.setState({
        seedUploadList: e
      });

      return e.slice(-1);
    }

    this.setState({
      seedUploadList: e.fileList
    });

    // Limit the number of uploaded files to only one
    return e && e.fileList.slice(-1);
  };

  // When a new file has been added, this method will be triggered.
  handleFileUploadManually = file => {
    // We should allow file upload to work as usual
    this.skipFileUpload = false;

    // Return false means handle file upload manually.
    return false;
  };

  normalizeContractFile = e => {
    if (Array.isArray(e)) {
      this.setState({
        contactUploadList: e
      });

      return e.slice(-1);
    }

    this.setState({
      contractUploadList: e.fileList
    });

    // Limit the number of uploaded files to only one
    return e && e.fileList.slice(-1);
  };

  // Due to and-design's input component, it will store number as string even the type props has been set.
  // The below validation methods works first.
  // If the input pass the validation, it will convent to number by `convertStringToNumber`.
  convertStringToNumber = e => {
    const convertedValue = Number(e.currentTarget.value);
    if (isNaN(convertedValue)) {
      return 1;
    } else {
      return convertedValue;
    }
  };

  checkMaxSizeGB = (rule, value, callback) => {
    const number = Number(value);
    const maxNum = Math.pow(2, 23) - 1;

    if (!Number.isInteger(number) || number < 0) {
      callback('The number should be an positive integer');
    } else if (number > maxNum) {
      callback(`The number should not be larger than ${maxNum}`);
    } else {
      callback();
    }
  };

  checkMaxSafeInteger = (rule, value, callback) => {
    const number = Number(value);

    if (!Number.isInteger(number) || number < 1) {
      callback('The number should be an positive integer');
    } else if (number > Number.MAX_SAFE_INTEGER) {
      callback(`The number should not be larger than ${Number.MAX_SAFE_INTEGER}`);
    } else {
      callback();
    }
  };

  checkMaxTotalCPUsGtePerNode = (rule, value, callback) => {
    const maxTotalCPUs = Number(value);
    const maxCPUCore = this.props.form.getFieldValue('MaxCPUCore');
    if (
      value === undefined ||
      value === null ||
      value === '' ||
      maxCPUCore === undefined ||
      maxCPUCore === null ||
      maxCPUCore === ''
    ) {
      return callback();
    }
    const perNode = Number(maxCPUCore);
    if (!Number.isNaN(maxTotalCPUs) && !Number.isNaN(perNode) && maxTotalCPUs < perNode) {
      callback('Max Total CPUs must be greater than or equal to Max CPUs Per Node');
    } else {
      callback();
    }
  };

  checkIsDateValid = (rule, value, callback) => {
    const expiryDate = value;
    const today = moment().startOf('day');

    if (!expiryDate.isAfter(today, 'day')) {
      callback('The date you picked is invalid. Please pick any date after today.');
    } else {
      callback();
    }
  };

  checkIsSeedUploadSuccess = async (rule, value, callback) => {
    // The value of upload is a file list
    const seedFile = value[0] ? value[0].originFileObj : undefined;
    const { uploadSeed } = this.props;

    if (!this.skipFileUpload && seedFile) {
      await uploadSeed(seedFile);
    }

    // Get error after uploading file
    const { seedUploadList } = this.state;
    const { seedUploadError } = this.props;

    if (seedUploadList.length !== 0 && seedUploadError) {
      callback('This seed file is invalid');
    } else {
      callback();
    }
  };

  checkIsContractUploadSuccess = async (rule, value, callback) => {
    // The value of upload is a file list
    const contractFile = value[0] ? value[0].originFileObj : undefined;
    const { uploadContract } = this.props;

    if (!this.skipFileUpload && contractFile) {
      await uploadContract(contractFile);
    }

    // Get error after uploading file
    const { contractUploadList } = this.state;
    const { contractError } = this.props;

    if (contractUploadList.length !== 0 && contractError) {
      callback(contractError.data.Result);
    } else {
      callback();
    }
  };

  handleReset = () => {
    this.props.form.resetFields();

    this.setState({
      seedUploadList: [],
      contractUploadList: [],
      selectedUsePurpose: this.props.Use
    });

    this.isNewEntryForSeed = false;
    this.isNewEntryForContract = false;
  };

  validDaysExtraMsg = () => {
    const { isAdmin, maxLicenseDay } = this.props;

    const today = moment().startOf('day');
    const expiryDate = this.props.form.getFieldValue('ValidDays');

    // Show extra message when the duration of requested license is larger than `maxLicenseDay`
    // and the user is not administrator
    return !isAdmin && expiryDate.diff(today, 'days') > maxLicenseDay ? (
      <span className={styles.highlightMsg}>
        This license request needs to be approved by admin
      </span>
    ) : null;
  };

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevState.seedUploadList.length !== this.state.seedUploadList.length) {
      this.isNewEntryForSeed = false;
    }

    if (prevState.contractUploadList.length !== this.state.contractUploadList.length) {
      this.isNewEntryForContract = false;
    }
  }

  render() {
    const { getFieldDecorator } = this.props.form;
    const { selectedType, selectedUsePurpose, seedUploadList } = this.state;
    const { maxLicenseDay, signature } = this.props;

    const {
      Use: use,
      Audience: audience,
      GraphStudio: gst,
      Insights: insights,
      GSQLShell: gsqlshell,
      GraphQL: graphql,
      MaxGraph: graph,
      MaxClusterNode: clusterNode,
      MaxTopologySizeByte: topology,
      MaxEdge: edge,
      MaxVertex: vertex,
      MaxCPUCore: cpu,
      MaxTotalCPUs: maxTotalCPUs,
      CRR: crr,
      DataHA: dataHA,
      MaxPhysicalMemorySizeByte: phyMem,
      MaxRSSMemorySizeByte: rssMem,
      Signature: signatureInParam,
      ContractID: contractIDInParam
    } = this.props;

    const renderSignature = () => {
      return this.isNewEntryForSeed && signatureInParam
        ? signatureInParam
        : signature.length !== 0
        ? signature
        : [];
    };

    const formItemLayout = {
      labelCol: { span: 8 },
      wrapperCol: { span: 14 }
    };

    return (
      <Form {...formItemLayout} colon={false} onSubmit={this.handleSubmit}>
        <Form.Item label="Type">
          {getFieldDecorator('Type', {
            initialValue: this.state.selectedType,
            hidden: true,
            rules: [
              {
                required: true
              }
            ]
          })(
            <Radio.Group buttonStyle="solid" onChange={this.handleLicenseTypeSelect}>
              <Tooltip title={'For TigerGraph 3.x Engine and GraphStudio'}>
                <Radio.Button value={LICENSE_TYPE_JWT}>JWT</Radio.Button>
              </Tooltip>

              <Tooltip title={'For TigerGraph 2.x Engine'}>
                <Radio.Button value="string">String</Radio.Button>
              </Tooltip>

              <Tooltip title={'For TigerGraph 2.x GraphStudio'}>
                <Radio.Button value="gst">GraphStudio</Radio.Button>
              </Tooltip>
            </Radio.Group>
          )}
        </Form.Item>

        <Form.Item
          label={
            <span>
              Use for &nbsp;
              <Tooltip title="Please choose an option for what the license is used for">
                <Icon type="info-circle" />
              </Tooltip>
            </span>
          }
        >
          {getFieldDecorator('Use', {
            initialValue: use || 'Internal',
            rules: [
              {
                required: true,
                type: 'string'
              }
            ]
          })(
            <Select onChange={this.handleLicenseUsePurposeChange}>
              <Option value="Internal">Internal</Option>
              <Option value="Free Trial">Free Trial</Option>
              <Option value="Proof of Concept">Proof of Concept</Option>
              <Option value="Development">Development</Option>
              <Option value="Customer Production">Customer Production</Option>
              <Option value="Other">Other</Option>
            </Select>
          )}
        </Form.Item>

        {/* Use `hidden` option to prevent field validation and data collection */}
        {licensePayloadConfig[selectedType].Audience ? (
          <Form.Item
            label={
              <span>
                Audience &nbsp;
                <Tooltip title="Who will use the license">
                  <Icon type="info-circle" />
                </Tooltip>
              </span>
            }
          >
            {getFieldDecorator('Audience', {
              initialValue: audience || '',
              hidden: !licensePayloadConfig[selectedType].Audience,
              rules: [
                {
                  required: true,
                  type: 'string',
                  message: 'Please input the name who will use the license',
                  whitespace: true
                },
                {
                  max: 50,
                  message: 'The length of audience should be less than 50'
                }
              ]
            })(
              <Input
                autoComplete={'off'}
                placeholder="Please input the name who will use the license"
              />
            )}
          </Form.Item>
        ) : null}

        {licensePayloadConfig[selectedType].ValidDays ? (
          <Form.Item label="Expiry Date" extra={<this.validDaysExtraMsg />}>
            {getFieldDecorator('ValidDays', {
              initialValue: moment().add(maxLicenseDay, 'days'),
              hidden: !licensePayloadConfig[selectedType].ValidDays,
              rules: [
                {
                  required: true,
                  type: 'object',
                  message: 'Please choose the expiration of the license'
                },
                {
                  validator: this.checkIsDateValid
                }
              ]
            })(<DatePicker />)}
          </Form.Item>
        ) : null}

        {licensePayloadConfig[selectedType].ContractID ? (
          <Form.Item
            label={
              <span className="upload-tips">
                Contract &nbsp;
                <Tooltip title={<div>Upload contract PDF for backup</div>}>
                  <Icon type="info-circle" />
                </Tooltip>
              </span>
            }
          >
            {getFieldDecorator('ContractID', {
              // When this license request is from clone button with `contractID`
              // and its use is `Customer Production`, we allow this field to be hidden
              // for skipping to trigger required validation check
              // because its initial value is empty array.
              hidden:
                this.isNewEntryForContract && contractIDInParam
                  ? selectedUsePurpose === 'Customer Production'
                  : !licensePayloadConfig[selectedType].ContractID,
              valuePropName: 'fileList',
              initialValue: [],
              getValueFromEvent: this.normalizeContractFile,
              rules: [
                {
                  required: selectedUsePurpose === 'Customer Production',
                  message: 'Contract file is required'
                },
                {
                  validator: this.checkIsContractUploadSuccess
                }
              ]
            })(
              <Upload listType="text" beforeUpload={this.handleFileUploadManually}>
                <Button>
                  <Icon type="upload" /> Click to upload contract PDF
                </Button>
              </Upload>
            )}
          </Form.Item>
        ) : null}

        {licensePayloadConfig[selectedType].GraphStudio ? (
          <Form.Item label="Enable GraphStudio">
            {getFieldDecorator('GraphStudio', {
              initialValue: gst !== undefined ? gst : true,
              valuePropName: 'checked',
              hidden: !licensePayloadConfig[selectedType].GraphStudio,
              rules: [
                {
                  type: 'boolean'
                }
              ]
            })(<Switch />)}
          </Form.Item>
        ) : null}
        {licensePayloadConfig[selectedType].Insights ? (
          <Form.Item label="Enable Insights">
            {getFieldDecorator('Insights', {
              initialValue: insights !== undefined ? insights : true,
              valuePropName: 'checked',
              hidden: !licensePayloadConfig[selectedType].Insights,
              rules: [
                {
                  type: 'boolean'
                }
              ]
            })(<Switch />)}
          </Form.Item>
        ) : null}
        {licensePayloadConfig[selectedType].GSQLShell ? (
          <Form.Item label="Enable GSQL Shell">
            {getFieldDecorator('GSQLShell', {
              initialValue: gsqlshell !== undefined ? gsqlshell : true,
              valuePropName: 'checked',
              hidden: !licensePayloadConfig[selectedType].GSQLShell,
              rules: [
                {
                  type: 'boolean'
                }
              ]
            })(<Switch />)}
          </Form.Item>
        ) : null}
        {licensePayloadConfig[selectedType].GraphQL ? (
          <Form.Item label="Enable GraphQL">
            {getFieldDecorator('GraphQL', {
              initialValue: graphql !== undefined ? graphql : true,
              valuePropName: 'checked',
              hidden: !licensePayloadConfig[selectedType].GraphQL,
              rules: [
                {
                  type: 'boolean'
                }
              ]
            })(<Switch />)}
          </Form.Item>
        ) : null}

        {licensePayloadConfig[selectedType].MaxGraph ? (
          <Form.Item label="Max Number of Graph">
            {getFieldDecorator('MaxGraph', {
              initialValue: graph || 1000,
              hidden: !licensePayloadConfig[selectedType].MaxGraph,
              rules: [
                {
                  type: 'integer',
                  message: 'The number should be an integer'
                }
              ]
            })(<InputNumber min={1} max={Number.MAX_SAFE_INTEGER} />)}
          </Form.Item>
        ) : null}

        {licensePayloadConfig[selectedType].MaxClusterNode ? (
          <Form.Item label="Max Number of Cluster Nodes">
            {getFieldDecorator('MaxClusterNode', {
              initialValue: clusterNode || 1024,
              hidden: !licensePayloadConfig[selectedType].MaxClusterNode,
              rules: [
                {
                  type: 'integer',
                  message: 'The number should be an integer'
                }
              ]
            })(<InputNumber min={1} max={Number.MAX_SAFE_INTEGER} precision={0} />)}
          </Form.Item>
        ) : null}

        {licensePayloadConfig[selectedType].MaxTopologySizeByte ? (
          <Form.Item
            label={
              <span>
                Max Topology in GB &nbsp;
                <Tooltip title="Leave unchanged for unlimited">
                  <Icon type="info-circle" />
                </Tooltip>
              </span>
            }
            extra="Topology size represents the total combined size of both the topology data and the vector data."
          >
            {getFieldDecorator('MaxTopologySizeByte', {
              initialValue:
                byteConversion(topology, 'GB') < 1
                  ? Math.pow(2, 23) - 1
                  : byteConversion(topology, 'GB') || Math.pow(2, 23) - 1,
              hidden: !licensePayloadConfig[selectedType].MaxTopologySizeByte,
              rules: [
                {
                  validator: this.checkMaxSizeGB
                }
              ],
              getValueFromEvent: this.convertStringToNumber
            })(<Input type="number" addonAfter="GB" />)}
          </Form.Item>
        ) : null}

        {licensePayloadConfig[selectedType].MaxEdge ? (
          <Form.Item
            label={
              <span>
                Max Number of Edges &nbsp;
                <Tooltip title="Leave unchanged for unlimited">
                  <Icon type="info-circle" />
                </Tooltip>
              </span>
            }
          >
            {getFieldDecorator('MaxEdge', {
              initialValue: edge || Number.MAX_SAFE_INTEGER,
              hidden: !licensePayloadConfig[selectedType].MaxEdge,
              rules: [
                {
                  validator: this.checkMaxSafeInteger
                }
              ],
              getValueFromEvent: this.convertStringToNumber
            })(<Input type="number" addonAfter="Edges" />)}
          </Form.Item>
        ) : null}

        {licensePayloadConfig[selectedType].MaxVertex ? (
          <Form.Item
            label={
              <span>
                Max Number of Vertices &nbsp;
                <Tooltip title="Leave unchanged for unlimited">
                  <Icon type="info-circle" />
                </Tooltip>
              </span>
            }
          >
            {getFieldDecorator('MaxVertex', {
              initialValue: vertex || Number.MAX_SAFE_INTEGER,
              hidden: !licensePayloadConfig[selectedType].MaxVertex,
              rules: [
                {
                  validator: this.checkMaxSafeInteger
                }
              ],
              getValueFromEvent: this.convertStringToNumber
            })(<Input type="number" addonAfter="Vertices" />)}
          </Form.Item>
        ) : null}

        {licensePayloadConfig[selectedType].MaxCPUCore ? (
          <Form.Item label="Max CPUs Per Node">
            {getFieldDecorator('MaxCPUCore', {
              initialValue: cpu || 1024,
              hidden: !licensePayloadConfig[selectedType].MaxCPUCore,
              rules: [
                {
                  type: 'integer',
                  message: 'The number should be an integer'
                }
              ]
            })(
              <InputNumber
                min={1}
                max={Number.MAX_SAFE_INTEGER}
                precision={0}
                onChange={() => this.props.form.validateFields(['MaxTotalCPUs'], () => {})}
              />
            )}
          </Form.Item>
        ) : null}

        {licensePayloadConfig[selectedType].MaxTotalCPUs ? (
          <Form.Item label="Max Total CPUs">
            {getFieldDecorator('MaxTotalCPUs', {
              initialValue: maxTotalCPUs !== undefined ? maxTotalCPUs : 1024,
              hidden: !licensePayloadConfig[selectedType].MaxTotalCPUs,
              rules: [
                {
                  type: 'integer',
                  message: 'The number should be an integer'
                },
                {
                  validator: this.checkMaxTotalCPUsGtePerNode
                }
              ]
            })(<InputNumber min={1} max={Number.MAX_SAFE_INTEGER} precision={0} />)}
          </Form.Item>
        ) : null}

        {licensePayloadConfig[selectedType].CRR ? (
          <Form.Item label="CRR">
            {getFieldDecorator('CRR', {
              initialValue: crr !== undefined ? crr : true,
              valuePropName: 'checked',
              hidden: !licensePayloadConfig[selectedType].CRR,
              rules: [
                {
                  type: 'boolean'
                }
              ]
            })(<Switch />)}
          </Form.Item>
        ) : null}
        {licensePayloadConfig[selectedType].DataHA ? (
          <Form.Item label="HA">
            {getFieldDecorator('DataHA', {
              initialValue: dataHA !== undefined ? dataHA : true,
              valuePropName: 'checked',
              hidden: !licensePayloadConfig[selectedType].DataHA,
              rules: [
                {
                  type: 'boolean'
                }
              ]
            })(<Switch />)}
          </Form.Item>
        ) : null}

        {licensePayloadConfig[selectedType].MaxPhysicalMemorySizeByte ? (
          <Form.Item
            label="Max Physical Memory"
            extra={'The maximum physical memory of the cluster'}
          >
            {getFieldDecorator('MaxPhysicalMemorySizeByte', {
              initialValue: byteConversion(phyMem, 'GB') || 2 ** 20,
              hidden: !licensePayloadConfig[selectedType].MaxPhysicalMemorySizeByte,
              rules: [
                {
                  validator: this.checkMaxSafeInteger
                }
              ],
              getValueFromEvent: this.convertStringToNumber
            })(
              <Input
                type="number"
                addonAfter="GB"
                placeholder={'Please input the maximum physical memory'}
              />
            )}
          </Form.Item>
        ) : null}

        {licensePayloadConfig[selectedType].MaxRSSMemorySizeByte ? (
          <Form.Item
            label={
              <span>
                Max RSS Memory &nbsp;
                <Tooltip title={'Resident set size - Wikipedia'}>
                  <a
                    className={styles.linkInFieldName}
                    href="https://en.wikipedia.org/wiki/Resident_set_size"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon type="info-circle" />
                  </a>
                </Tooltip>
              </span>
            }
            extra={'The maximum RSS of TigerGraph user of the cluster'}
          >
            {getFieldDecorator('MaxRSSMemorySizeByte', {
              initialValue: byteConversion(rssMem, 'GB') || 2 ** 20,
              hidden: !licensePayloadConfig[selectedType].MaxRSSMemorySizeByte,
              rules: [
                {
                  validator: this.checkMaxSafeInteger
                }
              ],
              getValueFromEvent: this.convertStringToNumber
            })(
              <Input
                type="number"
                addonAfter="GB"
                placeholder={'Please input the resident set size memory'}
              />
            )}
          </Form.Item>
        ) : null}

        {selectedUsePurpose === 'Customer Production' ? (
          <Form.Item
            label={
              <span>
                Customer Emails &nbsp;
                <Tooltip title="Enter customer email addresses, separated by commas">
                  <Icon type="info-circle" />
                </Tooltip>
              </span>
            }
          >
            {getFieldDecorator('CustomerEmails', {
              initialValue: [],
              rules: [
                {
                  required: true,
                  message: 'Please input customer email addresses'
                },
                {
                  validator: (rule, value, callback) => {
                    if (!value || value.length === 0) {
                      callback('Please input at least one customer email address');
                    } else {
                      callback();
                    }
                  }
                }
              ]
            })(
              <Select
                mode="tags"
                style={{ width: '100%' }}
                placeholder="Enter customer email addresses"
                tokenSeparators={[',']}
              />
            )}
          </Form.Item>
        ) : null}

        {selectedUsePurpose === 'Customer Production' ? (
          <Form.Item
            label={
              <span>
                CC Emails &nbsp;
                <Tooltip title="Enter CC email addresses, separated by commas">
                  <Icon type="info-circle" />
                </Tooltip>
              </span>
            }
          >
            {getFieldDecorator('CC', {
              initialValue: [],
              rules: [
                {
                  required: false,
                  message: 'Please input CC email addresses'
                }
              ]
            })(
              <Select
                mode="tags"
                style={{ width: '100%' }}
                placeholder="Enter service manager email addresses"
                tokenSeparators={[',']}
              />
            )}
          </Form.Item>
        ) : null}

        {licensePayloadConfig[selectedType].Seed ? (
          <Form.Item
            label={
              <span className="upload-tips">
                Seed &nbsp;
                <Tooltip title={<div>Optional, host information for binding hardware</div>}>
                  <Icon type="info-circle" />
                </Tooltip>
              </span>
            }
          >
            {getFieldDecorator('Seed', {
              hidden: true,
              valuePropName: 'fileList',
              initialValue: [],
              getValueFromEvent: this.normalizeSeedFile,
              rules: [
                {
                  validator: this.checkIsSeedUploadSuccess
                }
              ]
            })(
              <Upload listType="text" beforeUpload={this.handleFileUploadManually}>
                <Button>
                  <Icon type="upload" /> Click to upload seed file
                </Button>
              </Upload>
            )}
          </Form.Item>
        ) : null}

        {/* (this.isNewEntryForSeed || seedUploadList.length !== 0)
         * 1. `this.isNewEntryForSeed` for displaying signature in paramJSON from clone button
         * 2. if `seedUploadList` has files, depending on its length to display signature or not
         */}

        {selectedType === LICENSE_TYPE_JWT &&
        (this.isNewEntryForSeed || seedUploadList.length !== 0) &&
        renderSignature().length !== 0 ? (
          <Collapse bordered={false}>
            <Panel header={'Signature'} key={1} style={customPanelStyle}>
              <div className={styles.codeBlock}>
                <SyntaxHighlighter language="json" style={tomorrow}>
                  {JSON.stringify(renderSignature(), undefined, 2)}
                </SyntaxHighlighter>
              </div>
            </Panel>
          </Collapse>
        ) : null}

        <Form.Item wrapperCol={{ span: 6, offset: 8 }}>
          <div>
            <Button onClick={this.handleReset}>Reset</Button>
            <Button
              className="little-margin-left"
              type="primary"
              htmlType="submit"
              disabled={seedUploadList.length !== 0 && signature.length === 0}
            >
              Submit
            </Button>
          </div>
        </Form.Item>
      </Form>
    );
  }
}

const LicenseCreateFormWrap = Form.create({ name: 'license-creation' })(LicenseCreateForm);

export default LicenseCreateFormWrap;
